import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import merge from 'lodash.merge';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import colors from 'components/PortfolioTables/colors';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { convertCompositionPiechartData, alterColor } from 'helpers/chart';
import { FinancialInstitution } from 'pages/Accounts/Edit/components/ExternalProducts/model';
import { grayscale } from 'helpers/chart';

interface Props {
  pieDataset: { [id: string]: number };
  seriesName?: string;
  settings?: Highcharts.Options;
  title?: string;
  type: 'asset' | 'region' | 'currency' | 'finInst';
  finInst?: { [id: string]: FinancialInstitution };
  pdf?: boolean;
  original?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    fixChart: (props: { pdf?: boolean }) => ({
      margin: '8px 8px',
      maxWidth: props.pdf ? 'calc(50% - 16px)' : 'calc(25% - 16px)',
      width: props.pdf ? 'calc(50% - 16px)' : 'calc(25% - 16px)',
      position: 'relative',

      '@media (max-width: 1023px)': {
        maxWidth: 'calc(50% - 16px)',
        width: 'calc(50% - 16px)',
      },
      '@media (max-width: 640px)': {
        maxWidth: 'calc(100% - 16px)',
        width: 'calc(100% - 16px)',
      },

      '& .highcharts-container': {
        /*margin: '0 auto',*/
        width: '100%',
        '& svg': {
          width: '100%',
        },
      },

      '& .highcharts-tooltip path': {
        display: 'none',
      },
      '& .highcharts-legend': props.pdf
        ? undefined
        : {
            translate: '27%',
            marginLeft: '27%',
          },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(1), & .highcharts-legend > div > div > div:first-child:nth-last-child(1)':
        {
          translate: '0 -120px',
        },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(2), & .highcharts-legend > g > g > g:first-child:nth-last-child(2) ~ g, & .highcharts-legend > div > div > div:first-child:nth-last-child(2), & .highcharts-legend > div > div > div:first-child:nth-last-child(2) ~ div':
        {
          translate: '0 -100px',
        },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(3), & .highcharts-legend > g > g > g:first-child:nth-last-child(3) ~ g, & .highcharts-legend > div > div > div:first-child:nth-last-child(3), & .highcharts-legend > div > div > div:first-child:nth-last-child(3) ~ div':
        {
          translate: '0 -80px',
        },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(4), & .highcharts-legend > g > g > g:first-child:nth-last-child(4) ~ g, & .highcharts-legend > div > div > div:first-child:nth-last-child(4), & .highcharts-legend > div > div > div:first-child:nth-last-child(4) ~ div':
        {
          translate: '0 -60px',
        },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(5), & .highcharts-legend > g > g > g:first-child:nth-last-child(5) ~ g, & .highcharts-legend > div > div > div:first-child:nth-last-child(5), & .highcharts-legend > div > div > div:first-child:nth-last-child(5) ~ div':
        {
          translate: '0 -40px',
        },
      '& .highcharts-legend > g > g > g:first-child:nth-last-child(6), & .highcharts-legend > g > g > g:first-child:nth-last-child(6) ~ g, & .highcharts-legend > div > div > div:first-child:nth-last-child(6), & .highcharts-legend > div > div > div:first-child:nth-last-child(6) ~ div':
        {
          translate: '0 -20px',
        },
      '& .highcharts-legend-item > span': {
        fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif !important`,
        top: '2px !important',
      },
      '& ._tooltip': {
        zIndex: 1000,
        '& > span > span': {
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          display: 'inline-block',
          fontSize: '0.8em',
          textAlign: 'center',
          boxSizing: 'border-box',
          borderRadius: '10px',
          border: 'solid 2px #000',
        },
      },
    }),
  }),
);

const titleFormatter = (title: string, mobile?: boolean) => {
  return `<div style="text-align: center; font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; font-size: 18px; font-weight: 800;">${title.replace(
    /\s/g,
    '<br />',
  )}</div>`;
};

const PieGraph: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles({ pdf: props.pdf });
  const mobile = useMediaQuery('@media (max-width: 640px)', { noSsr: true });
  const small = useMediaQuery('@media (max-width: 359px)', { noSsr: true });
  const data = convertCompositionPiechartData(props.pieDataset, props.type, props.finInst || {}, props.original);

  const defaultSettings: Highcharts.Options = {
    chart: {
      type: 'pie',
      spacingTop: 0,
      spacingBottom: 0,
      marginBottom: 100,
      backgroundColor: 'transparent',
      animation: !props.pdf,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: titleFormatter(props.title || ''),
      verticalAlign: 'middle',
      align: 'center',
      useHTML: true,
      y: -30,
      style: {
        zIndex: 10,
      },
    },
    tooltip: {
      borderRadius: 20,
      borderWidth: 2,
      shadow: false,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      useHTML: true,
      className: '_tooltip',
      formatter: function (this) {
        return `<span class="${this.point.name}">${t(this.point.name)
          .toUpperCase()
          .replace(' ', '<br />')}<br /><span style="font-size: 1.5em; font-weight: bold">${this.y} %</span></span>`;
      },
      style: {
        zIndex: 15,
      },
    },
    plotOptions: {
      series: {
        animation: !props.pdf,
      },
      pie: {
        allowPointSelect: false,
        point: {
          events: {
            legendItemClick: function () {
              return false;
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,

        size: '59%',
      },
    },
    legend: {
      align: 'left',
      layout: 'vertical',
      verticalAlign: 'bottom',
      symbolWidth: 14,
      symbolHeight: 14,
      symbolRadius: 14,
      itemMarginBottom: 4,
      itemStyle: { lineHeight: '16px' },
      floating: true,
      useHTML: true,
      labelFormatter: function () {
        return this.name + ` (${Math.round((this as Highcharts.Point).percentage || 0)} %)`;
      },
      x: props.pdf ? 70 : undefined,
    },

    series: [
      {
        id: 'main',
        name: props.seriesName || '',
        type: 'pie',
        data: data,
        slicedOffset: 0,
        dataLabels: {
          connectorColor: '#ccc',
          connectorShape: 'fixedOffset',
          softConnector: false,
          connectorWidth: 2,
          useHTML: true,
          formatter: function (this) {
            return `${t(this.point.name).toUpperCase().replace(' ', '<br />')}`;
          },
        },
        //size: '50%',
        //innerSize: '77%',
        size: 190,
        innerSize: 150,
      },
      {
        id: 'shadow',
        name: props.seriesName || '',
        type: 'pie',
        data: data.map((d) => {
          return {
            name: d.name,
            y: d.y,
            color:
              props.type === 'finInst' && d.name !== 'Edward'
                ? props.original
                  ? grayscale(alterColor(d.color, -25))
                  : alterColor(d.color, -25)
                : props.original
                ? alterColor(d.color, -25)
                : colors[d.color],
          };
        }),
        shadow: false,
        borderColor: 'transparent',
        borderWidth: 3,
        showInLegend: false,
        slicedOffset: 0,
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: false,

        innerSize: 140,
        size: 151,
      },
    ],
    responsive: {
      rules: [
        {
          condition: { callback: () => mobile },
          chartOptions: {
            legend: {
              y: 30,
            },
            series: [
              { id: 'main', size: 220, innerSize: 180, type: 'pie' },
              { id: 'shadow', size: 170, innerSize: 181, type: 'pie' },
            ],
          },
        },
        {
          condition: { callback: () => small },
          chartOptions: {
            legend: {
              y: 0,
            },
            series: [
              { id: 'main', size: 190, innerSize: 150, type: 'pie' },
              { id: 'shadow', size: 140, innerSize: 151, type: 'pie' },
            ],
          },
        },
      ],
    },
  };

  const graphSettings = props.settings ? merge({}, defaultSettings, props.settings) : defaultSettings;

  return (
    <div className={classes.fixChart}>
      <HighchartsReact highcharts={Highcharts} options={graphSettings} />
    </div>
  );
};

export default React.memo(PieGraph);
